import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MESSAGES } from '@app/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateImageMutation, useDeleteImageMutation } from '@modules/images';
import { useCreateProductMutation, useLazyGetProductByIdQuery, useUpdateProductMutation } from '@modules/product';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';
import { Image } from '@modules/product/types';

import { IFlowProcess, IProductFormData, OnChangeFunctionProps } from './helper/types';
import { createProductSchema, initialFlowState, initialFormState } from './helper/data';
import { onChangeUpload, handleDeleteImage } from './helper/localHelper';
import { prepareClonedData, prepareImageUploadPromises } from './helper/utils';

export const useProductLogic = () => {
  const { id } = useParams()

  const [createProduct] = useCreateProductMutation()
  const [updateProduct] = useUpdateProductMutation()
  const [createImage] = useCreateImageMutation()
  const [deleteImage] = useDeleteImageMutation()
  const [getProductById] = useLazyGetProductByIdQuery();
  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<IProductFormData>({
    // @ts-ignore
    resolver: yupResolver(createProductSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const images = watch('uploadedFileList')
  const existingImagesWatch = watch('existingImages')

  const icons = watch('uploadedIconList')
  const existingIconssWatch = watch('existingIcons')

  const previews = useRenderFileViewHook(images, existingImagesWatch)
  const previewsIcons = useRenderFileViewHook(icons, existingIconssWatch)

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: IProductFormData) => {
    handleLoadingState(true);

    let imagePaths: string[] = [];
    let iconPaths: string[] = [];

    try {
      const imageUploadPromises = prepareImageUploadPromises(data.uploadedFileList, createImage);
      const iconUploadPromises = prepareImageUploadPromises(data.uploadedIconList, createImage);

      const uploadedImages = await Promise.all(imageUploadPromises || []);
      const uploadedIcons = await Promise.all(iconUploadPromises || []);

      imagePaths = uploadedImages?.map((result) => result?.id || '');
      iconPaths = uploadedIcons?.map((result) => result?.id || '');

      const clonedData = prepareClonedData(data, imagePaths, iconPaths);

      if (id) {
        await updateProduct({ id, body: clonedData }).unwrap()
        getProductData()
      } else {
        await createProduct(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id ? MESSAGES.blogHasBeenSuccessfullyUpdated : MESSAGES.blogHasBeenSuccessfullyCreated);
    } catch (error: any) {
      if (iconPaths?.length) {
        // If after first step, when FileList uploaded in backend.
        // And then before product creation appears some problems.
        // In that case we delete all uploaded images

        iconPaths.forEach(async (imageId) => {
          await deleteImage({ id: imageId })
        })
      }

      if (imagePaths?.length) {
        // If after first step, when FileList uploaded in backend.
        // And then before product creation appears some problems.
        // In that case we delete all uploaded images

        imagePaths.forEach(async (imageId) => {
          await deleteImage({ id: imageId })
        })
      }

      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getProductData = async () => {
    if (!id) {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))

      return
    }

    try {
      const product = await getProductById(id).unwrap()

      const clonedImages = JSON.parse(JSON.stringify(product?.images)) as Image[]

      clonedImages?.sort((a, b) => a.sort - b.sort);

      reset({
        ...product,
        categoryId: (product as any)?.categoryId || (product as any)?.category?.id || null,
        existingIcons: product.icons,
        existingImages: clonedImages,
        mainImageUrl: product.images.find((image) => image.isMain)?.url || null,
      });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getProductData()
  }, [id])

  return {
    ...flowProcess,
    paramId: id,
    methods,
    errors,
    watch,
    setValue,
    setError,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    handleDeleteImage: (bas64: string) => handleDeleteImage({
      bas64,
      previews,
      images,
      watch,
      setValue,
      setState: setFlowProcess,
    }),
    onChangeUpload: (props: OnChangeFunctionProps) => onChangeUpload({
      watch,
      setValue,
      setState: setFlowProcess,
      setError,
      ...props,
    }),
  }
}
